import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { blogApi } from '../../services/api';

function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await blogApi.getAll(false); // Include drafts
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading blog posts:', error);
      alert('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (postId) => {
    navigate(`/admin/blog/${postId}/edit`);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      return;
    }

    try {
      await blogApi.delete(postId);
      alert('Blog post deleted successfully!');
      loadPosts(); // Reload the list
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post');
    }
  };

  const togglePublish = async (postId, currentStatus) => {
    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      const updatedPost = {
        ...post,
        is_published: !currentStatus,
        published_at: !currentStatus ? new Date().toISOString() : null
      };

      await blogApi.update(postId, updatedPost);

      alert(`Blog post ${!currentStatus ? 'published' : 'unpublished'} successfully!`);
      loadPosts(); // Reload the list
    } catch (error) {
      console.error('Error updating blog post status:', error);
      alert('Failed to update blog post status');
    }
  };

  const viewPost = (slug) => {
    window.open(`/insights/${slug}`, '_blank');
  };

  const getStatusBadge = (isPublished) => {
    return isPublished
      ? { color: 'bg-green-100 text-green-800', text: 'Published' }
      : { color: 'bg-yellow-100 text-yellow-800', text: 'Draft' };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blog Management</h1>
            <p className="text-gray-600">Manage your blog posts and insights</p>
          </div>
          <Link
            to="/admin/blog/new"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Add New Post
          </Link>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total Posts</p>
            <p className="text-2xl font-bold">{posts.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Published</p>
            <p className="text-2xl font-bold text-green-600">
              {posts.filter(p => p.is_published).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Drafts</p>
            <p className="text-2xl font-bold text-yellow-600">
              {posts.filter(p => !p.is_published).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Categories</p>
            <p className="text-2xl font-bold">
              {[...new Set(posts.map(p => p.category))].length}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading blog posts...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Published
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => {
                    const status = getStatusBadge(post.is_published);
                    return (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{post.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {post.excerpt || 'No excerpt provided'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            {post.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => togglePublish(post.id, post.is_published)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${status.color} hover:opacity-80`}
                          >
                            {status.text}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {post.published_at
                            ? new Date(post.published_at).toLocaleDateString()
                            : 'Not published'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(post.id)}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium px-3 py-1 bg-blue-50 rounded hover:bg-blue-100"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => viewPost(post.slug)}
                              className="text-green-600 hover:text-green-900 text-sm font-medium px-3 py-1 bg-green-50 rounded hover:bg-green-100"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium px-3 py-1 bg-red-50 rounded hover:bg-red-100"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && posts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📝</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No blog posts yet</h3>
            <p className="text-gray-500 mb-6">Create your first blog post to share insights with your audience.</p>
            <Link
              to="/admin/blog/new"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Create Your First Post
            </Link>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2">How to Manage Blog Posts</h3>
          <ul className="space-y-2 text-sm text-blue-700">
            <li>• <strong>Edit</strong> - Click edit to modify blog post content and settings</li>
            <li>• <strong>Publish/Unpublish</strong> - Click the status button to publish or unpublish posts</li>
            <li>• <strong>View</strong> - Open the blog post in a new tab to preview it</li>
            <li>• <strong>Delete</strong> - Permanently remove a blog post (use with caution)</li>
            <li>• <strong>Categories</strong>: Leadership, Startups, Ecosystem, Growth, Entrepreneurship, Career</li>
            <li>• <strong>Save as Draft</strong> - Work on posts without publishing them</li>
            <li>• <strong>Publish</strong> - Make posts visible on the website</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminBlog;